import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3, Calendar, Activity, FileText, GitCompare, Video,
  Users, Shield, Smartphone, Settings, Bell, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoModal from "@/components/DemoModal";
import AuthModal from "@/components/AuthModal";

const featureCategories = [
  {
    category: "Tracking",
    items: [
      {
        icon: Calendar,
        title: "Attendance Tracking",
        desc: "Automated session attendance with QR codes, manual override, and absence notifications. View attendance trends across weeks, months, and seasons.",
        tags: ["Automated", "QR Code", "Alerts"],
      },
      {
        icon: Activity,
        title: "Training Session Logs",
        desc: "Log every session with drill types, intensity, duration, and coach notes. Build a comprehensive training history for each player and team.",
        tags: ["Session Types", "Custom Drills", "Notes"],
      },
      {
        icon: Users,
        title: "Squad Management",
        desc: "Organize players into groups, positions, and age categories. Track player development pathways from juniors to seniors.",
        tags: ["Groups", "Positions", "History"],
      },
    ],
  },
  {
    category: "Analytics",
    items: [
      {
        icon: BarChart3,
        title: "Player Performance Dashboard",
        desc: "Visual dashboards showing speed, endurance, technical skills, and tactical awareness. Track progression with weekly and monthly trend charts.",
        tags: ["Charts", "Trends", "Benchmarks"],
      },
      {
        icon: GitCompare,
        title: "Team Comparison Analytics",
        desc: "Compare players side by side or against team averages. Identify outliers, consistent performers, and those needing additional support.",
        tags: ["Comparison", "Benchmarks", "Rankings"],
      },
      {
        icon: Activity,
        title: "Match & Drill Analytics",
        desc: "Break down performance by drill type and match position. Understand which training approaches deliver the best results.",
        tags: ["Drills", "Match Analysis", "KPIs"],
      },
    ],
  },
  {
    category: "Reporting",
    items: [
      {
        icon: FileText,
        title: "Progress Reports",
        desc: "Auto-generate professional PDF reports for individual players, teams, or the whole academy. Schedule automatic delivery to parents and stakeholders.",
        tags: ["PDF Export", "Scheduled", "Custom Templates"],
      },
      {
        icon: Bell,
        title: "Parent Portal",
        desc: "Dedicated portal where parents can view their child's attendance, progress, and upcoming sessions. Increase transparency and trust.",
        tags: ["Parent Access", "Notifications", "History"],
      },
      {
        icon: Video,
        title: "Video Analysis & Tagging",
        desc: "Upload match and training footage. Tag key moments, link clips to performance data, and share highlight reels with players.",
        tags: ["Upload", "Tagging", "Clips"],
      },
    ],
  },
  {
    category: "Management",
    items: [
      {
        icon: Smartphone,
        title: "Mobile Coach App",
        desc: "Log sessions, take attendance, and review player stats from the pitch side. Full iOS and Android app with offline capability.",
        tags: ["iOS", "Android", "Offline"],
      },
      {
        icon: Settings,
        title: "Admin Panel",
        desc: "Complete academy management: billing, staff roles, permissions, and system settings. Multi-coach, multi-team support.",
        tags: ["Multi-Coach", "Permissions", "Billing"],
      },
      {
        icon: Shield,
        title: "Data Security & Privacy",
        desc: "GDPR-compliant platform with role-based access control. Your player data is encrypted at rest and in transit.",
        tags: ["GDPR", "Encrypted", "Role-Based"],
      },
    ],
  },
];

export default function FeaturesPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <div className="bg-gray-900 pt-32 pb-20" data-testid="features-page-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-widest text-forest/70 font-bold mb-4">
            Platform Features
          </p>
          <h1
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Every Tool You Need to{" "}
            <span className="text-forest/70">Coach Smarter</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
            From pitch-side attendance to deep performance analytics, NextTackle has everything your
            academy needs to develop elite players.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => setDemoOpen(true)}
              data-testid="features-demo-btn"
              className="bg-forest hover:bg-forest/90 text-white rounded-sm px-8 py-3 h-auto"
            >
              Request Demo <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              onClick={() => setAuthOpen(true)}
              data-testid="features-trial-btn"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 rounded-sm px-8 py-3 h-auto bg-transparent"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </div>

      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {featureCategories.map((cat) => (
            <div
              key={cat.category}
              className="mb-20"
              data-testid={`feature-category-${cat.category.toLowerCase()}`}
            >
              <h2
                className="text-2xl font-bold text-gray-900 mb-8 pb-4 border-b border-gray-200"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {cat.category}
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {cat.items.map((item) => (
                  <div
                    key={item.title}
                    className="p-8 border border-gray-200 rounded-sm hover:border-green-400 hover:shadow-md transition-all duration-200 group"
                    data-testid={`feature-${item.title.toLowerCase().replace(/ /g, "-")}`}
                  >
                    <div className="w-12 h-12 bg-forest/5 rounded-sm flex items-center justify-center mb-5 group-hover:bg-forest transition-colors">
                      <item.icon className="w-6 h-6 text-forest group-hover:text-white transition-colors" />
                    </div>
                    <h3
                      className="text-lg font-bold text-gray-900 mb-3"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{item.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="py-20 bg-forest text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-4xl font-black text-white mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Ready to see it in action?
          </h2>
          <p className="text-green-100 mb-8">
            Get a personalized demo tailored to your academy's needs.
          </p>
          <Button
            onClick={() => setDemoOpen(true)}
            className="bg-white text-green-700 hover:bg-gray-50 rounded-sm px-8 py-3 h-auto font-bold"
            data-testid="features-bottom-demo-btn"
          >
            Request Your Demo <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab="register" />
    </div>
  );
}
